import request from '@/util/request'
import _ from 'lodash'
import refineryUtils from '@/util/refinery'
import {getSpotPriceTypeForTransaction} from '@/util/refinery'
import {optionsToPaginationParams} from '@/util/pagination'

const {formatBalance, normalizeTransactionPayload} = refineryUtils

const accountCodeMap = {
  metal_balance: 'balance',
  cash_balance: 'balance',
  locked_metal: 'locked',
  locked_cash: 'locked',
  pending_metal_in: 'pending_in',
  pending_cash_in: 'pending_in',
  external_cash_in: 'external_in',
  external_metal_in: 'external_in',
  pending_metal_out: 'pending_out',
  pending_cash_out: 'pending_out',
  internal_transfer_out: 'pending_out',
  external_metal_clearing: 'external_clearing',
  external_cash_clearing: 'external_clearing',
  customer_held_cash: 'held',
  customer_held_metal: 'held',
  external_cash_terminal: 'external_terminal',
  external_metal_terminal: 'external_terminal',
  unallocated_metal: 'unallocated',
  unallocated_cash: 'unallocated',
  metal_outflows: 'outflows',
  cash_outflows: 'outflows',
}

const instrumentPriorityMap = {
  'AU': 0,
  'AG': 1,
  'PT': 2,
  'PD': 3,
  'USD': 4,
}

const PAGINATION_DEFAULTS = {
  PAGE_SIZE: 10,
  INITIAL_PAGE: 1,
}

// Helper functions
const validateAdjustmentData = ({ currencyValue, metalWeight, memo }) => {
  const data = {}
  
  if (!_.isNil(currencyValue)) {
    const numVal = parseFloat(currencyValue)
    if (!isNaN(numVal)) {
      data.currencyValue = numVal
    } else {
      throw new Error('Invalid currency value provided for adjustment: expected number.')
    }
  }
  
  if (!_.isNil(metalWeight)) {
    const numVal = parseFloat(metalWeight)
    if (!isNaN(numVal)) {
      data.metalWeight = numVal
    } else {
      throw new Error('Invalid metal weight provided for adjustment: expected number.')
    }
  }
  
  if (memo) {
    data.memo = String(memo)
  }
  
  return data
}

const state = {
  journalEntries: [],
  ledgerAccounts: [],
  metalBalances: [],
  metalTransactions: {
    data: {
      rows: [],
      total: 0,
      page: PAGINATION_DEFAULTS.INITIAL_PAGE,
      pageSize: PAGINATION_DEFAULTS.PAGE_SIZE,
    },
    loading: false,
    error: null,
  },
  transactionTypes: [
    'cash_advance',
    'cash_out',
    'metal_receipt',
    'cash_settlement',
    'metal_settlement',
    'metal_out',
    'metal_delivery',
    'external_deposit',
    'spot_lock',
    'internal_transfer'
  ],
  cashAdvances: [],
  accountSummary: null,
}

async function loadCashAdvances(getters, { companyId = null, status = null, allCompanies = null, refiningLotId = null } = {}) {
  const isAdmin = Boolean(getters.getUserProfile?.isAdmin)
  const adminModeEnabled = Boolean(getters.getAdminModeEnabled)
  const selectedCompanyId = getters.getSelectedCompany?.id
  const useGlobalAdminList = allCompanies === true
    || (allCompanies !== false && isAdmin && adminModeEnabled && !companyId)

  let url = `/refinery/metal-transactions`

  if (useGlobalAdminList) {
    url += `/cash-advances`
  } else {
    const resolvedCompanyId = companyId || selectedCompanyId
    if (!resolvedCompanyId) {
      return null
    }
    url += `/${resolvedCompanyId}/cash-advances`
  }

  const params = { status }
  if (refiningLotId) {
    params.refiningLotId = refiningLotId
  }

  const apiResponse = await request({
    url,
    method: 'get',
    params,
  })
  const responseBody = apiResponse?.response ?? apiResponse
  return responseBody?.rows != null
    ? { rows: responseBody.rows, can: responseBody.can ?? {} }
    : { rows: Array.isArray(responseBody) ? responseBody : [], can: {} }
}

const getters = {
  journalEntries: (state) => state.journalEntries,
  ledgerAccounts: (state) => state.ledgerAccounts,
  metalBalances: (state) => {
    const grouped = _.groupBy(state.metalBalances, account => {
      if (account.metalSymbol && !account.currencyType) return account.metalSymbol
      if (account.currencyType && !account.metalSymbol) return account.currencyType
      return account.currencyType + account.metalSymbol
    })

    return Object.entries(grouped).map(([instrumentKey, accounts]) => {
      const unit = accounts[0].currencyType ? accounts[0].currencyType : 'ozt'

      // Define initial state for the reduction
      const initialAccumulator = {
        netTotal: 0,
        maxTimestamp: 0, // Track latest update timestamp
        columns: Object.values(accountCodeMap).reduce((cols, key) => ({ ...cols, [key]: 0 }), {}),
      }

      // Single pass reduction
      const summary = accounts.reduce((acc, account) => {
        const numericBalance = parseFloat(account.balance) || 0
        const mappedKey = accountCodeMap[account.accountCode]
        const timestamp = account.updatedAt ? new Date(account.updatedAt).getTime() : 0

        // Define account codes to exclude from net total calculation
        const excludeFromTotal = [
          'external_metal_in',
          // 'external_metal_clearing',
          // 'external_metal_terminal',
          'external_cash_in',
          // 'external_cash_clearing',
          // 'external_cash_terminal',
          'customer_held_cash', 
          'customer_held_metal',
          'unallocated_metal',
          'unallocated_cash',
          'metal_outflows',
          'cash_outflows'
        ]

        // Update net total, excluding specific account codes
        if (!excludeFromTotal.includes(account.accountCode)) {
          acc.netTotal += (account.accountType === 'liability' ? -numericBalance : numericBalance)
        }

        // Accumulate column values
        if (mappedKey && acc.columns.hasOwnProperty(mappedKey)) {
          acc.columns[mappedKey] += numericBalance
        }

        // Store allocated spot locks data (only need to capture it once per instrument)
        if (account.allocatedSpotLocks !== undefined && acc.allocatedSpotLocks === undefined) {
          acc.allocatedSpotLocks = account.allocatedSpotLocks
        }

        // Store spot lock summary with locked USD values (only need to capture once per instrument)
        if (account.spotLockSummary !== undefined && acc.spotLockSummary === undefined) {
          acc.spotLockSummary = account.spotLockSummary
        }

        // Update max timestamp
        acc.maxTimestamp = Math.max(acc.maxTimestamp, timestamp)

        return acc
      }, initialAccumulator)

      // Build the final formatted object
      const result = {
        instrument: instrumentKey,
        units: unit,
        updatedAt: summary.maxTimestamp ? new Date(summary.maxTimestamp).toISOString() : null,
        total: formatBalance(summary.netTotal.toFixed(4), unit),
      }

      // Add formatted column values
      for (const [key, value] of Object.entries(summary.columns)) {
         // Optionally add check here: if (value !== 0) { ... }
         result[key] = formatBalance(value.toFixed(4), unit)
      }

      // Add allocated spot locks data if available (for metals only)
      if (summary.allocatedSpotLocks !== undefined && instrumentKey !== 'USD') {
        result.allocated_spot_locks = formatBalance(summary.allocatedSpotLocks.toFixed(4), unit)
      }

      // Add spot lock summary with locked USD values (for metals only)
      // This allows frontend to display fixed locked prices instead of dynamic market prices
      if (summary.spotLockSummary !== undefined && instrumentKey !== 'USD') {
        result.spotLockSummary = summary.spotLockSummary
      }

      return result

    }).sort((a, b) => instrumentPriorityMap[a.instrument] - instrumentPriorityMap[b.instrument])
  },
  metalTransactions: (state) => state.metalTransactions,
  transactionTypes: (state) => state.transactionTypes,
  cashAdvances: (state) => state.cashAdvances,
  cashAdvancesRows: (state) =>
    state.cashAdvances?.rows ?? (Array.isArray(state.cashAdvances) ? state.cashAdvances : []),
  canApproveCashAdvances: (state) => Boolean(state.cashAdvances?.can?.approveCashAdvances),
  hasReportingAccess: (state) => Boolean(state.metalTransactions?.data?.can?.reportingAccess),
  getAccountSummary: (state, getters, rootState, rootGetters) => {
    const selectedCompanyId = rootGetters.getSelectedCompany?.id
    if (
      state.accountSummary?.companyId &&
      selectedCompanyId &&
      state.accountSummary.companyId !== selectedCompanyId
    ) {
      return null
    }
    return state.accountSummary
  },
}

const actions = {
  async setupCustomerLedgerAccounts({ getters, commit, dispatch }) {
    try {
      const response = await request({
        url: `/refinery/accounts/${getters.getSelectedCompany.id}/setup/customer`,
        method: 'post',
        data: {
          currencyType: 'USD',
        },
      })
      commit('SET_LEDGER_ACCOUNTS', response.result)
      dispatch('fetchMetalBalances')
      dispatch('fetchLedgerAccounts')
      dispatch('fetchJournalEntries')
    } catch (error) {
      console.error('Error setting up company ledger accounts:', error)
    }
  },
  async setupCounterPartyLedgerAccounts({ getters, commit, dispatch }) {
    try {
      const response = await request({
        url: `/refinery/accounts/${getters.getSelectedCompany.id}/setup/counterparty`,
        method: 'post',
        data: {
          currencyType: 'USD',
        },
      })
      commit('SET_LEDGER_ACCOUNTS', response.result)
      dispatch('fetchMetalBalances')
      dispatch('fetchLedgerAccounts')
      dispatch('fetchJournalEntries')
    } catch (error) {
      console.error('Error setting up counterparty ledger accounts:', error)
    }
  },
  async setupSystemLedgerAccounts({ getters, commit, dispatch }) {
    try {
      const response = await request({
        url: `/refinery/accounts/${getters.getSelectedCompany.id}/setup/system`,
        method: 'post',
        data: {
          currencyType: 'USD',
        },
      })
      commit('SET_LEDGER_ACCOUNTS', response.result)
      dispatch('fetchMetalBalances')
      dispatch('fetchLedgerAccounts')
      dispatch('fetchJournalEntries')
    } catch (error) {
      console.error('Error setting up system ledger accounts:', error)
    }
  },
  async fetchJournalEntries({ getters, commit }) {
    try {
      const response = await request({
        url: `/refinery/journal-entries/${getters.getSelectedCompany.id}`,
        method: 'get',
      })
      commit('SET_JOURNAL_ENTRIES', response)
    } catch (error) {
      console.error('Error fetching journal entries:', error)
    }
  },
  async fetchLedgerAccounts({ getters, commit }) {
    try {
      const response = await request({
        url: `/refinery/accounts/${getters.getSelectedCompany.id}`,
        method: 'get',
      })
      commit('SET_LEDGER_ACCOUNTS', response)
    } catch (error) {
      console.error('Error fetching ledger accounts:', error)
    }
  },
  async fetchMetalBalances({getters, commit}){
    try {
      const response = await request({
        url: `/refinery/balances/${getters.getSelectedCompany.id}`,
        method: 'get',
      })
      commit('SET_METAL_BALANCES', response)
    } catch (error) {
      console.error('Error fetching metal balances:', error)
    }
  },
  async fetchAccountSummary({getters, commit}) {
    try {
      const response = await request({
        url: `/refinery/account-summary/${getters.getSelectedCompany.id}`,
        method: 'get',
      })
      commit('SET_ACCOUNT_SUMMARY', response)
      return response
    } catch (error) {
      console.error('Error fetching account summary:', error)
      commit('SET_ACCOUNT_SUMMARY', null)
      throw error
    }
  },
  clearAccountSummary({commit}) {
    commit('SET_ACCOUNT_SUMMARY', null)
  },
  async fetchMetalTransactions({getters, commit}) {
    try {
      const response = await request({
        url: `/refinery/metal-transactions/${getters.getSelectedCompany.id}`,
        method: 'get',
      })
      commit('SET_METAL_TRANSACTIONS', response)
    } catch (error) {
      console.error('Error fetching metal transactions:', error)
      commit('SET_METAL_TRANSACTIONS', [])
    }
  },
  async fetchRelevantMetalTransactions({getters, commit}) {
    try {
      const response = await request({
        url: `/refinery/metal-transactions/${getters.getSelectedCompany.id}/relevant`,
        method: 'get',
      })
      commit('SET_METAL_TRANSACTIONS', response)
    } catch (error) {
      console.error('Error fetching relevant metal transactions:', error)
      commit('SET_METAL_TRANSACTIONS', [])
    }
  },
  async calculateSpotLockFulfillment({getters}, {metalSymbol, metalWeight, direction = 'sell'}) {
    try {
      const response = await request({
        url: `/refinery/metal-transactions/${getters.getSelectedCompany.id}/spot-locks/settlement-calculation`,
        method: 'post',
        data: {
          settlementRequest: {
            [metalSymbol]: parseFloat(metalWeight)
          }
        }
      })
      return response.settlementCalculations[metalSymbol] || null
    } catch (error) {
      console.error('Error calculating spot lock fulfillment', error)
      return null
    }
  },
  // New paginated fetch used by TransactionsTable component
  async fetchCompanyTransactions({ getters, commit }, { options = {}, query = {} }) {
    try {
      const paginationParams = optionsToPaginationParams(options)
      const companyId = getters.getSelectedCompany.id
      const response = await request({
        url: `/refinery/metal-transactions/${companyId}/relevant`,
        method: 'get',
        params: { ...paginationParams, ...query },
      })
      commit('SET_METAL_TRANSACTIONS', response)
    } catch (error) {
      console.error('Error fetching paginated transactions:', error)
      commit('SET_METAL_TRANSACTIONS', [])
    }
  },
  async createTransaction({ getters, dispatch }, {payload, spotPrices}) {
    const priceType = getSpotPriceTypeForTransaction(payload.type, payload.direction)

    if (payload.type === 'cash_advance') {
      payload.metalWeight = 0
    }
    if ([
      'metal_settlement',
      'metal_out',
      'metal_receipt',
    ].includes(payload.type)) {
      payload.currencyValue = 0
    }

    try {
      const response = await request({
        url: `/refinery/metal-transactions/${getters.getSelectedCompany.id}`,
        method: 'post',
        data: {
          ...normalizeTransactionPayload(payload),
          snapshotSpotPriceGold: parseFloat(spotPrices.Gold[priceType]),
          snapshotSpotPriceSilver: parseFloat(spotPrices.Silver[priceType]),
          snapshotSpotPricePlatinum: parseFloat(spotPrices.Platinum[priceType]),
          snapshotSpotPricePalladium: parseFloat(spotPrices.Palladium[priceType]),
        },
      })
      dispatch('fetchMetalTransactions')
      dispatch('fetchMetalBalances')
      dispatch('fetchLedgerAccounts')
      dispatch('fetchJournalEntries')
      return response // Return the response so caller can get transaction ID if needed
    } catch (error) {
      console.error('Error creating transaction:', error)
      throw error // Re-throw the error so the caller can handle it
    }
  },
  async createCustomerCashOut({ getters, dispatch }, {payload, spotPrices}) {
    try {
      const response = await request({
        url: `/refinery/customer-cash-out/${getters.getSelectedCompany.id}`,
        method: 'post',
        data: {
          companyId: getters.getSelectedCompany.id,
          memo: payload.memo,
          snapshotSpotPriceGold: parseFloat(spotPrices.Gold.bid),
          snapshotSpotPriceSilver: parseFloat(spotPrices.Silver.bid),
          snapshotSpotPricePlatinum: parseFloat(spotPrices.Platinum.bid),
          snapshotSpotPricePalladium: parseFloat(spotPrices.Palladium.bid),
          metalSymbol: payload.metalSymbol,
          metalWeight: payload.metalWeight,
          metalUnits: payload.metalUnits,
          currencyType: payload.currencyType,
          currencyValue: payload.currencyValue,
          spotLocksToFulfill: payload.spotLocksToFulfill || [],
          newSpotLocksRequired: payload.newSpotLocksRequired || {}
        },
      })
      dispatch('fetchMetalTransactions')
      dispatch('fetchMetalBalances')
      dispatch('fetchLedgerAccounts')
      dispatch('fetchJournalEntries')
      return response
    } catch (error) {
      console.error('Error creating customer cash out:', error)
      throw error
    }
  },
  async createCustomerSpotLock({ getters, dispatch }, payload) {
    try {
      const response = await request({
        url: `/refinery/customer-spot-locks/${getters.getSelectedCompany.id}`,
        method: 'post',
        data: payload,
      })
      dispatch('fetchMetalTransactions')
      dispatch('fetchMetalBalances')
      dispatch('fetchLedgerAccounts')
      dispatch('fetchJournalEntries')
      return response
    } catch (error) {
      console.error('Error creating customer spot lock:', error)
      throw error
    }
  },
  async updateTransaction({ commit, getters, dispatch }, { id, status, memo, companyId, refiningLot = null }) {
    try {
      const data = {
        status,
        memo,
      };

      if (refiningLot) {
        data.refiningLot = refiningLot;
      }

      const {result} = await request({
        url: `/refinery/metal-transactions/${companyId || getters.getSelectedCompany.id}/${id}`,
        method: 'patch',
        data,
      })
      // These fetches ensure the UI reflects the changes
      dispatch('fetchRelevantMetalTransactions');
      dispatch('fetchMetalBalances');
      dispatch('fetchLedgerAccounts');
      dispatch('fetchJournalEntries');
      commit('SET_METAL_TRANSACTION', result) // Update the single transaction in store, if it's there
    } catch (error) {
      console.error('Error updating transaction:', error)
      throw error
    }
  },

  async processCashOut({ getters, dispatch, rootGetters }, { metalSymbol, metalWeight, currencyValue, memo, spotPrices, spotLockFulfillment }) {
    try {
      // Get spot prices from the provided parameter or fall back to store
      const activeSpotPrices = spotPrices || rootGetters.getSpotPrices || {}
      
      // Extract individual spot prices for the API
      const snapshotSpotPriceGold = activeSpotPrices.Gold?.bid || 0
      const snapshotSpotPriceSilver = activeSpotPrices.Silver?.bid || 0
      const snapshotSpotPricePlatinum = activeSpotPrices.Platinum?.bid || 0
      const snapshotSpotPricePalladium = activeSpotPrices.Palladium?.bid || 0

      const response = await request({
        url: `/refinery/cash-out/${getters.getSelectedCompany.id}`,
        method: 'post',
        data: {
          companyId: getters.getSelectedCompany.id,
          metalSymbol,
          metalWeight,
          currencyValue,
          memo,
          snapshotSpotPriceGold,
          snapshotSpotPriceSilver,
          snapshotSpotPricePlatinum,
          snapshotSpotPricePalladium,
          spotLockFulfillment
        }
      })

      // Refresh data after successful cash out
      dispatch('fetchRelevantMetalTransactions')
      dispatch('fetchMetalBalances')
      dispatch('fetchLedgerAccounts')
      dispatch('fetchJournalEntries')

      return response
    } catch (error) {
      console.error('Error processing cash out:', error)
      throw error
    }
  },
  
  async refreshTransactionData({ dispatch }) {
    await Promise.all([
      dispatch('fetchRelevantMetalTransactions'),
      dispatch('fetchMetalBalances'),
      dispatch('fetchLedgerAccounts'),
      dispatch('fetchJournalEntries')
    ])
  },
  
  async adjustTransaction({ commit, getters, dispatch }, params) {
    try {
      const { id, companyId } = params
      const data = validateAdjustmentData(params)
      
      const { result } = await request({
        url: `/refinery/metal-transactions/${companyId || getters.getSelectedCompany.id}/${id}/adjust`,
        method: 'post',
        data,
      })
      
      commit('SET_METAL_TRANSACTION', result)
      
      // Initialize guidance if allocation changes detected
      if (result.allocationChanges?.length > 0) {
        dispatch('spotLockGuidance/initializeGuidance', {
          adjustmentResult: result,
          originalTransaction: params
        }, { root: true })
      }
      
      await dispatch('refreshTransactionData')
      return result
    } catch (error) {
      console.error('Error adjusting transaction:', error)
      throw error
    }
  },
  async fetchCashAdvances({ commit, getters }, { companyId = null, status = null, allCompanies = null, refiningLotId = null } = {}) {
    try {
      const payload = await loadCashAdvances(getters, { companyId, status, allCompanies, refiningLotId })
      if (!payload) {
        return null
      }
      commit('SET_CASH_ADVANCES', payload)
      return payload
    } catch (error) {
      console.error('Error fetching cash advances:', error)
      throw error
    }
  },
  async fetchCashAdvancesSnapshot(_context, { companyId = null, status = null, allCompanies = null, refiningLotId = null } = {}) {
    const payload = await loadCashAdvances(_context.getters, { companyId, status, allCompanies, refiningLotId })
    return payload?.rows ?? []
  },
  async approveCashAdvance({ dispatch }, { refiningLotId, companyId, metalTransactionId, skipRefetch = false }) {
    try {
      await request({
        url: `/refinery/lots/${companyId}/${refiningLotId}/approve-advance`,
        method: 'post',
        data: {
          refiningLotId,
          companyId,
          metalTransactionId,
        },
      })
      if (!skipRefetch) {
        dispatch('fetchCashAdvances')
      }
    } catch (error) {
      throw error
    }
  },
  async acceptCashAdvance({ dispatch }, { refiningLotId, companyId, metalTransactionId, skipRefetch = false }) {
    try {
      await request({
        url: `/refinery/lots/${companyId}/${refiningLotId}/accept-advance`,
        method: 'post',
        data: {
          refiningLotId,
          companyId,
          metalTransactionId,
        },
      })
      if (!skipRefetch) {
        dispatch('fetchCashAdvances')
      }
    } catch (error) {
      throw error
    }
  },
  async syncMetalBalancesToNetSuite({ getters }) {
    try {
      const response = await request({
        url: '/refinery/metal-balances/ns-sync',
        method: 'post',
        data: {
          companyId: getters.getSelectedCompany.id,
        },
      })
      return response
    } catch (error) {
      throw error
    }
  },
}

const mutations = {
  SET_JOURNAL_ENTRIES(state, entries) {
    state.journalEntries = entries
  },
  SET_LEDGER_ACCOUNTS(state, accounts) {
    state.ledgerAccounts = accounts
  },
  SET_METAL_BALANCES(state, balances) {
    state.metalBalances = balances
  },
  SET_ACCOUNT_SUMMARY(state, accountSummary) {
    state.accountSummary = accountSummary
  },
  SET_METAL_TRANSACTIONS(state, transactions) {
    state.metalTransactions = transactions
  },
  SET_CASH_ADVANCES(state, cashAdvances) {
    state.cashAdvances = cashAdvances
  },
  SET_LOADING(state, loading) {
    state.loading = loading
  },
  SET_METAL_TRANSACTION(state, updatedTransaction) {
    const index = state.metalTransactions?.data?.rows?.findIndex(t => t.id === updatedTransaction.id)
    if (index !== -1) {
      state.metalTransactions.data.rows.splice(index, 1, updatedTransaction)
    }
  },
}

export default {
  namespaced: false,
  state,
  getters,
  actions,
  mutations,
}